import { FC } from "react";

import { Grid, Text } from "theme-ui";

import { Page } from "src/components/layout";
import { useExtensionsQuery } from "src/graphql";
import { Row } from "src/ui/box";
import { Card } from "src/ui/card";
import { Heading } from "src/ui/heading";
import {
  AlertingIcon,
  BitbucketIcon,
  CheckCircleIcon,
  DatadogIcon,
  DBTIcon,
  GitHubIcon,
  GitIcon,
  GitlabIcon,
  LookerIcon,
  MonitoringIcon,
  PagerDutyIcon,
  SlackIcon,
} from "src/ui/icons";
import { IconProps } from "src/ui/icons/icon";
import { Link } from "src/ui/link";
import { PageSpinner } from "src/ui/loading";

export const Extensions: FC = () => {
  const { data, isLoading: loading } = useExtensionsQuery();

  if (loading) {
    return <PageSpinner />;
  }

  return (
    <Page crumbs={[{ label: "Extensions" }]} size="medium">
      <Heading sx={{ mb: 4 }} variant="h1">
        Extensions
      </Heading>

      <Text sx={{ color: "base.5", mb: 10, fontSize: 2 }}>Connect your favorite tools to get more out of Hightouch</Text>

      <Grid columns="repeat(auto-fit, 350px)" gap={6} sx={{ maxWidth: "1200px" }}>
        <ExtensionCard
          description="Get notified about important events"
          icon={AlertingIcon}
          integrations={[SlackIcon, PagerDutyIcon]}
          route="alerting"
          setup={Boolean(data?.alerting?.length)}
          title="Alerting"
        />
        <ExtensionCard
          description="Access dbt models stored in a Git repo"
          icon={DBTIcon}
          route="dbt-models"
          setup={Boolean(data?.dbtModels?.length)}
          title="dbt Models"
        />
        <ExtensionCard
          description="Schedule syncs triggered by dbt Cloud"
          icon={DBTIcon}
          route="dbt-cloud"
          setup={Boolean(data?.dbtCloud?.length)}
          title="dbt Cloud"
        />

        <ExtensionCard
          description="Bidirectionally manage resources with Git"
          icon={GitIcon}
          integrations={[GitHubIcon, BitbucketIcon, GitlabIcon]}
          route="git-sync"
          setup={Boolean(data?.gitSync?.[0]?.enabled)}
          title="Git Sync"
        />
        <ExtensionCard
          description="Monitor sync health with dashboards"
          icon={MonitoringIcon}
          integrations={[DatadogIcon]}
          route="monitoring"
          setup={Boolean(data?.monitoring?.length)}
          title="Monitoring"
        />
        <ExtensionCard
          description="Use Looks to power Hightouch models"
          icon={LookerIcon}
          route="looker"
          setup={Boolean(data?.looker?.length)}
          title="Looker"
        />
      </Grid>
    </Page>
  );
};

export const ExtensionCard: FC<{
  title: string;
  icon: FC<IconProps>;
  description: string;
  route: string;
  setup: boolean;
  integrations?: FC<IconProps>[];
}> = ({ title, icon: Icon, description, route, setup, integrations }) => {
  return (
    <Link sx={{ color: "initial" }} to={`/extensions/${route}${setup ? "/configuration" : ""}`}>
      <Card
        clickable
        footer={
          <Row sx={{ alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <Row sx={{ alignItems: "center" }}>
              {integrations?.map((Icon, i) => (
                <Icon key={i} size={20} sx={{ mr: 4 }} />
              ))}
            </Row>
            <Text sx={{ color: "ocean" }}>{setup ? "Manage" : "Set up extension"}</Text>
          </Row>
        }
        size="small"
      >
        <Row sx={{ alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Icon size={30} />
          <CheckCircleIcon color={setup ? "green" : "base.3"} />
        </Row>
        <Text sx={{ mb: 2, fontWeight: "bold", fontSize: 2 }}>{title}</Text>
        <Text sx={{ color: "base.5" }}>{description}</Text>
      </Card>
    </Link>
  );
};
